import React from 'react';
import {
  Box, Button, ButtonText, HStack, Text, VStack,
} from '@gluestack-ui/themed';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/common/auth/store';
import { getGetModulesQueryKey } from '@/common/api/endpoints/modules/modules';
import {
  retakeExamAttempt,
  upgradeSubmissionToOralExam,
} from '@/common/api/endpoints/submissions/submissions';
import { getGetTasksByCourseModuleQueryKey } from '@/common/api/endpoints/tasks/tasks';
import {
  describeExamAttemptStatus,
  type ExamAttemptNoteCopy,
  type ExamAttemptStatusPayload,
} from '@/student/grade/examAttemptCopy';

const TONE = {
  warning: { bg: '$statusWarningSubtle', fg: '$textOnWarningSubtle', accent: '$statusWarning' },
  error: { bg: '$statusErrorSubtle', fg: '$textOnErrorSubtle', accent: '$statusError' },
} as const;

interface ExamAttemptNoteProps {
  status?: ExamAttemptStatusPayload | null;
  busy?: boolean;
  error?: string | null;
  onRetake?: () => void;
  onUpgrade?: () => void;
}

export function ExamAttemptNoteView({
  copy, busy, error, onRetake, onUpgrade,
}: {
  copy: ExamAttemptNoteCopy;
  busy?: boolean;
  error?: string | null;
  onRetake?: () => void;
  onUpgrade?: () => void;
}) {
  const colors = TONE[copy.tone];
  const onPress = copy.action?.testID === 'exam-attempts-upgrade' ? onUpgrade : onRetake;

  return (
    <Box
      testID="exam-attempts-status"
      borderWidth={1}
      borderColor="$borderLight"
      borderRadius="$md"
      overflow="hidden"
      bg={colors.bg}
    >
      <HStack>
        <Box width={3} bg={colors.accent} />
        <VStack flex={1} p="$4" space="sm">
          <Text size="md" fontWeight="semibold" color={colors.fg} testID="exam-attempts-reason">
            {copy.headline}
          </Text>
          {copy.detail ? (
            <Text size="md" color={colors.fg} testID="exam-attempts-detail">{copy.detail}</Text>
          ) : null}
          {copy.action ? (
            <Button
              action="primary"
              size="md"
              mt="$2"
              alignSelf="flex-start"
              testID={copy.action.testID}
              onPress={onPress}
              isDisabled={busy}
            >
              <ButtonText textAlign="center">
                {busy ? 'Reopening module…' : copy.action.label}
              </ButtonText>
            </Button>
          ) : null}
          {error ? (
            <Text size="sm" color="$statusError" testID="exam-attempts-error">{error}</Text>
          ) : null}
        </VStack>
      </HStack>
    </Box>
  );
}

const ExamAttemptNote: React.FC<ExamAttemptNoteProps> = ({
  status, busy, error, onRetake, onUpgrade,
}) => {
  const copy = describeExamAttemptStatus(status);
  if (!copy) return null;
  return (
    <ExamAttemptNoteView
      copy={copy}
      busy={busy}
      error={error}
      onRetake={onRetake}
      onUpgrade={onUpgrade}
    />
  );
};

export function ExamAttemptGradeActions({
  module,
  courseId,
  navigation,
}: {
  module: { id: string; examAttempt?: ExamAttemptStatusPayload | null };
  courseId: string;
  navigation: { navigate: (screen: string, params?: unknown) => void };
}) {
  const userId = useAuthStore((state) => state.user?.id);
  const queryClient = useQueryClient();
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const run = async (call: () => Promise<unknown>) => {
    if (!userId) {
      setError('Sign in again, then try once more.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await call();
      await queryClient.invalidateQueries({
        queryKey: getGetTasksByCourseModuleQueryKey(courseId, module.id),
      });
      await queryClient.invalidateQueries({ queryKey: getGetModulesQueryKey() });
      navigation.navigate('ModuleScreen', { moduleId: module.id, courseId });
    } catch (caught) {
      const detail = (caught as { response?: { data?: { detail?: string } }; message?: string })
        ?.response?.data?.detail;
      setError(detail || 'That did not work. You are still on this report. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ExamAttemptNote
      status={module.examAttempt}
      busy={busy}
      error={error}
      onRetake={() => {
        run(() => retakeExamAttempt(userId || '', courseId, module.id)).catch(() => undefined);
      }}
      onUpgrade={() => {
        run(() => upgradeSubmissionToOralExam(userId || '', courseId, module.id)).catch(() => undefined);
      }}
    />
  );
}

export default ExamAttemptNote;
